/**
 * AttachModal — cross-scope attach modal (Upload new · Choose from library).
 *
 * Spec: `Specifications/FILES_SCOPE_AND_VISIBILITY.md` — §1 five scopes,
 *       §2 R-UP + R-GLOBAL, §3 R-PROV/R-MANAGE/R-COUNT, §4a two-path modal,
 *       §4b data model (references on the owner record, no listAttachments),
 *       §4 R-LIB-1/2/3 (library-sourced files are references, never copies).
 * Rulings cited: `DEC-60` (five scopes + two upload paths day one),
 *                `DEC-61` (camelCase for every new field a writer emits),
 *                `LEN-NV-01 N-13` (status→{label,tone} maps),
 *                `LEN-NV-01 N-14` (fallback-key governance).
 *
 * Prime directive (`AGENTS.md`): resolve, never restate. Do not paraphrase
 * the spec rules — cite them. If a rule proves wrong at build, change the
 * spec in the same commit, do not diverge silently.
 *
 * Provable-now / provable-later split (per operator ruling 2026-08-02):
 *   PROVABLE NOW (this commit):
 *     - Component renders both paths and their state transitions.
 *     - Scope parameterisation (customer | quote | job | workOrder | changeOrder).
 *     - owner.attachments[] reference read ("Choose from library").
 *     - Backend-error state when the deployed handler returns
 *       "File storage not configured" (S3 env var unset — CTO note pending).
 *     - R-PROV labels rendered.
 *     - R-MANAGE read-only for library-sourced references.
 *   PROVABLE LATER (boxed PARTIAL, per N-9):
 *     - Upload new → S3 PUT → confirmAttachment → owner.attachments[] write
 *       → reload-persist. Unprovable end-to-end until S3_BUCKET_FILES is set
 *       on the deployed Lambda. Unit tests mock the presign response and
 *       assert the full state machine including the failure branch.
 *
 * Entry-point CTAs stay disabled with existing honest copy until
 * FLAGS.ATTACHMENTS_UPLOAD is flipped. The modal itself is built and its
 * state machine is unit-tested behind the flag.
 *
 * Props:
 *   open           boolean                    controlled visibility
 *   onClose        () => void                 fires on Cancel / backdrop / ✕
 *   scope          Scope                      UI-facing scope (camelCase)
 *   ownerId        string                     scope-specific ID
 *   ownerLabel?    string                     human label for header
 *   companyId?     string                     needed for library reads
 *   allowLibrary?  boolean (default false)    IDI-55: show "Choose from library".
 *                                             On only for quote Review & Send and
 *                                             the WO Attachments card (Adrian 10-21).
 *   onAttached     (ref: AttachmentRef) => void
 *
 * AttachmentRef (DEC-61 — camelCase):
 *   { attachmentId, source: 'own'|'library', filename, contentType, sizeBytes, attachedAt }
 *
 * The AttachmentRef is one of two shapes owner-side attachment arrays hold.
 * See `utils/attachmentEntry.js` for the discriminator (`isAttachmentRef`)
 * and normalizer (`entryDisplay`) that every reader of `owner.attachments[]`
 * or legacy `owner.documents[]` should use — do not sniff `filename`/`name`
 * directly. Operator ruling 2026-08-03: declare the discriminator, don't
 * infer it. The rename `documents[] → attachments[]` is post-beta debt.
 *
 * Scope → owner_type (LEN-NV-01 N-14 fallback governance):
 *   The scope prop is camelCase (DEC-61); the backend enum on the wire is
 *   snake_case ('work_order') because that writer predates DEC-61 and is
 *   not being renamed this week. This is a KNOWN writer/reader mapping,
 *   not a fallback:
 *     - Canonical prop  : scope (UI-facing camelCase)
 *     - Wire value      : ownerType (snake_case, matches deployed handler)
 *     - Why it exists   : DEC-61 applies to NEW writers only; the B2
 *                         backend enum + `create_attachment` action are
 *                         pre-existing writers whose rename is post-beta.
 *     - On disagree     : compile-time map, not a runtime read, so
 *                         disagreement is impossible; unknown scope throws
 *                         before any wire call.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal } from '../ui/Slideover';
import { Button } from '../ui/Button';
import { useToast } from '../ui';
import { api as apiClient, settingsAPI } from '../../services/api';
import { useOptionalAuth } from '../../contexts/AuthContext';

// Scope → wire ownerType. Compile-time map (see docstring N-14 block).
const SCOPE_TO_OWNER_TYPE = Object.freeze({
  customer:  'customer',
  quote:     'quote',
  job:       'job',
  workOrder: 'work_order',
  // IDI-460: a change order owns its own documents[]. Before this,
  // ChangeOrderDetail.js passed scope="quote" for a CO, so the reference was
  // appended to a QUOTE record under the change order's id — across separate
  // uuid spaces that lands on no record at all, and the file was unreachable
  // from the change order. The backend gained the matching owner_type and a
  // companion write in the same PR.
  changeOrder: 'change_order',
});

const SCOPE_LABEL = Object.freeze({
  customer:  'customer',
  quote:     'quote',
  job:       'job',
  workOrder: 'work order',
  changeOrder: 'change order',
});

// Upload state machine — each transition is a testable moment.
const S = Object.freeze({
  IDLE:       'idle',
  PICKING:    'picking',
  PRESIGNING: 'presigning',
  UPLOADING:  'uploading',
  CONFIRMING: 'confirming',
  DONE:       'done',
  ERROR:      'error',
});

// MIME allowlist mirrors backend (`main.go:1442-1447`). Kept literal so
// misalignment is a visible diff, not a silent runtime rejection.
const ALLOWED_MIME = Object.freeze({
  'application/pdf': true,
  'image/png':       true,
  'image/jpeg':      true,
});

// Max upload size mirrors backend (10 MB, per `attachments.go:64` and its
// `maxUploadSizeBytes` constant).
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25MB — matches backend. S3 presigned PUT, no Lambda transit.

function isAllowedMime(mime) {
  return ALLOWED_MIME[mime] === true;
}

function humanBytes(n) {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Two-tab toggle. Renders as a segmented control at the top of the modal.
function TabToggle({ tab, onChange, disableLibrary, allowLibrary }) {
  const btn = (id, label, disabled) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      disabled={disabled}
      onClick={() => onChange(id)}
      className={[
        'px-3 py-1.5 text-sm rounded-md transition-colors',
        tab === id
          ? 'bg-white shadow-sm font-medium'
          : 'text-gray-600 hover:text-gray-900',
        disabled ? 'opacity-40 cursor-not-allowed' : '',
      ].join(' ')}
      style={tab === id ? { color: 'var(--color-primary)' } : undefined}
    >
      {label}
    </button>
  );
  return (
    <div
      role="tablist"
      aria-label="Attach source"
      className="inline-flex gap-1 p-1 rounded-md bg-gray-100 mb-4"
    >
      {btn('upload', 'Upload new', false)}
      {allowLibrary && btn('library', 'Choose from library', disableLibrary)}
    </div>
  );
}

// Upload path — state machine + file picker.
//
// idle → picking → presigning → uploading → confirming → done
//                                        ↘
//                                         error (retryable via reset)
//
// While the deployed handler's S3_BUCKET_FILES env var is unset, presigning
// resolves to a backend 500 with body {"message":"File storage not configured"}.
// That maps to ERROR and surfaces to the user — verified live on ACV Co.
// 2026-08-02.
function UploadPath({ scope, ownerId, ownerType, parentOwnerId, originType, originId, onAttached, onClose }) {
  const toast = useToast();
  // IDI-569: optional so isolated renders (tests) without an AuthProvider work.
  const signedInUserId = useOptionalAuth()?.user?.id || null;
  const [state, setState] = useState(S.IDLE);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const reset = () => {
    setState(S.IDLE);
    setFile(null);
    setError(null);
    setProgress(0);
  };

  const onPick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!isAllowedMime(f.type)) {
      setError(`Unsupported type: ${f.type || 'unknown'}. Allowed: PDF, PNG, JPG.`);
      setState(S.ERROR);
      return;
    }
    if (f.size > MAX_UPLOAD_BYTES) {
      setError(`File too large: ${humanBytes(f.size)}. Maximum ${humanBytes(MAX_UPLOAD_BYTES)}.`);
      setState(S.ERROR);
      return;
    }
    setFile(f);
    setState(S.PICKING);
  };

  const run = async () => {
    if (!file) return;
    try {
      setState(S.PRESIGNING);
      setError(null);

      const presign = await apiClient.getAttachmentUploadUrl({
        ownerType,
        ownerId,
        filename: file.name,
        contentType: file.type,
      });
      if (!presign || !presign.upload_url || !presign.key) {
        throw new Error('Presign response missing upload_url or key');
      }

      setState(S.UPLOADING);
      // Presigned PUT: no auth header; content-type MUST match the presign.
      // xhr used (not fetch) so we can report progress.
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presign.upload_url);
        xhr.setRequestHeader('Content-Type', file.type);
        // SCP on portica-dev requires SSE on PutObject; the presigned URL
        // signs this header, so the PUT must send it to match the signature.
        xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(file);
      });

      setState(S.CONFIRMING);
      const createResp = await apiClient.createAttachment({
        ownerType,
        ownerId,
        parentOwnerId,
        filename:    file.name,
        storageKey:  presign.key,
        contentType: file.type,
        sizeBytes:   file.size,
        sourceType:  'device',
        originType,
        originId,
      });
      if (!createResp || !createResp.id) {
        throw new Error('createAttachment response missing id');
      }

      // confirm_upload flips pending → active. Soft failure surface — the
      // record exists; a retry handler can re-run confirm later.
      try {
        await apiClient.confirmAttachment({ attachmentId: createResp.id });
      } catch (confirmErr) {
        // eslint-disable-next-line no-console
        console.warn('confirmAttachment failed (attachment record persists):', confirmErr);
      }

      // IDI-569: the local entry carries its author so JobDetail's own-file
      // Delete shows for a field tech without a reload.
      const authorUserId = createResp.author_user_id || createResp.authorUserId || signedInUserId;
      onAttached({
        attachmentId: createResp.id,
        source:       'own',
        ...(authorUserId ? { authorUserId } : {}),
        filename:     file.name,
        contentType:  file.type,
        sizeBytes:    file.size,
        attachedAt:   new Date().toISOString(),
        ...(originType ? { sourceType: originType, ...(originId ? { sourceId: originId } : {}) } : {}),
      });
      toast.success(`Attached ${file.name}`);
      setState(S.DONE);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Upload failed';
      setError(msg);
      setState(S.ERROR);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Upload a file to this {SCOPE_LABEL[scope] || 'record'}. PDF, PNG, or JPG.
        Maximum {humanBytes(MAX_UPLOAD_BYTES)}.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        onChange={onPick}
        disabled={state === S.PRESIGNING || state === S.UPLOADING || state === S.CONFIRMING}
        aria-label="Choose a file to upload"
      />

      {file && state === S.PICKING && (
        <div className="text-sm text-gray-700">
          Selected: <strong>{file.name}</strong> ({humanBytes(file.size)})
        </div>
      )}

      {(state === S.PRESIGNING || state === S.UPLOADING || state === S.CONFIRMING) && (
        <div className="text-sm text-gray-600">
          {state === S.PRESIGNING && 'Preparing upload…'}
          {state === S.UPLOADING && `Uploading… ${progress}%`}
          {state === S.CONFIRMING && 'Finishing…'}
        </div>
      )}

      {state === S.ERROR && error && (
        <div
          role="alert"
          className="text-sm p-3 rounded-md"
          style={{ background: 'var(--color-error-bg)', color: 'var(--color-error)' }}
        >
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {state === S.ERROR && (
          <Button variant="secondary" onClick={reset}>Try again</Button>
        )}
        <Button
          variant="primary"
          onClick={run}
          disabled={!file || state !== S.PICKING}
        >
          Upload
        </Button>
      </div>
    </div>
  );
}

// Library path — R-LIB-*.
//
// Reads company-scope files from settingsAPI.getList('documents') for now.
// Post-beta migration replaces this with a company.attachments[] read; the
// reference created here ({ attachmentId, source: 'library' }) is opaque to
// the consumer, so migrating storage does not change this component's shape.
// See FILES_SCOPE_AND_VISIBILITY.md §4 R-LIB-1.
//
// Attaching a library file creates a REFERENCE on the scope's attachments[],
// never a copy. R-LIB-2: removing this attachment removes the reference only.
// R-LIB-3: library-sourced attachments show provenance 'Library' (R-PROV)
// and are read-only wherever attached (R-MANAGE).
function LibraryPath({ scope, onAttached, onClose }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    settingsAPI
      .getList('documents')
      .then((rows) => {
        if (cancelled) return;
        setItems(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load company library');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const attach = () => {
    if (!selected) return;
    // Library rows carry: { id, name, type, size, data_url?, uploaded_at }
    // (legacy settings-list shape). Map to the canonical AttachmentRef.
    // When library storage migrates to B2, selected.id becomes an Attachment
    // id; consumer code is unchanged.
    onAttached({
      attachmentId: selected.id,
      source:       'library',
      filename:     selected.name || 'Untitled',
      contentType:  selected.type || 'application/octet-stream',
      sizeBytes:    Number(selected.size) || 0,
      attachedAt:   new Date().toISOString(),
    });
    toast.success(`Attached ${selected.name || 'file'} from library`);
    onClose();
  };

  if (loading) {
    return <div className="text-sm text-gray-600">Loading library…</div>;
  }

  if (error) {
    return (
      <div
        role="alert"
        className="text-sm p-3 rounded-md"
        style={{ background: 'var(--color-error-bg)', color: 'var(--color-error)' }}
      >
        {error}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-600 py-4">
        <p>The company library is empty.</p>
        <p className="mt-2 text-xs text-gray-500">
          Upload shared files in <strong>Settings › Documents</strong> so they
          can be attached to any {SCOPE_LABEL[scope] || 'record'}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Attach a file from the company library. The file stays owned by the
        company; this {SCOPE_LABEL[scope] || 'record'} gets a reference to it.
      </p>
      <ul
        role="listbox"
        aria-label="Company library files"
        className="border rounded-md divide-y max-h-64 overflow-y-auto"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {items.map((it) => {
          const isSelected = selected && selected.id === it.id;
          return (
            <li
              key={it.id}
              role="option"
              aria-selected={isSelected}
              onClick={() => setSelected(it)}
              className={[
                'px-3 py-2 cursor-pointer flex items-center justify-between text-sm',
                isSelected ? 'bg-gray-100' : 'hover:bg-gray-50',
              ].join(' ')}
            >
              <span className="truncate mr-3">{it.name || 'Untitled'}</span>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {humanBytes(Number(it.size) || 0)}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-end gap-2">
        <Button variant="primary" onClick={attach} disabled={!selected}>
          Attach
        </Button>
      </div>
    </div>
  );
}

// Main component. Composes the tab toggle over Upload/Library paths.
// IDI-484: `originType` / `originId` record where a file was attached FROM
// when it is stored on a different owner. The invoice page stores files on the
// job (scope="job") with originType="invoice" (ruled Q-45, 2026-10-11).
function AttachModal({ open, onClose, scope, ownerId, ownerLabel, companyId, onAttached, parentOwnerId, originType, originId, allowLibrary = false }) {
  const [tab, setTab] = useState('upload');

  // Reset tab whenever the modal transitions from closed → open.
  useEffect(() => {
    if (open) setTab('upload');
  }, [open]);

  const ownerType = useMemo(() => {
    const t = SCOPE_TO_OWNER_TYPE[scope];
    if (!t) throw new Error(`AttachModal: unknown scope "${scope}"`);
    return t;
  }, [scope]);

  const title = ownerLabel
    ? `Attach a file to ${ownerLabel}`
    : `Attach a file to this ${SCOPE_LABEL[scope] || 'record'}`;

  // IDI-465: there is no `company` scope any more. The company library is the
  // settings `documents` list (Settings -> Documents writes it, LibraryPath
  // reads it); an attachment with owner_type `company` was stored and
  // appended to nothing, so the backend now rejects it. An unknown scope
  // throws in `ownerType` above, which is what `company` now does.
  const disableLibrary = !companyId;

  return (
    <Modal open={open} onClose={onClose} title={title} width="md">
      {/* IDI-55: the library tab is not rendered at all unless allowLibrary. */}
      <TabToggle tab={tab} onChange={setTab} disableLibrary={disableLibrary} allowLibrary={allowLibrary} />
      {tab === 'upload' && (
        <UploadPath
          scope={scope}
          ownerId={ownerId}
          ownerType={ownerType}
          parentOwnerId={parentOwnerId}
          originType={originType}
          originId={originId}
          onAttached={onAttached}
          onClose={onClose}
        />
      )}
      {tab === 'library' && allowLibrary && !disableLibrary && (
        <LibraryPath
          scope={scope}
          onAttached={onAttached}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

export { AttachModal };
